import {useContext} from 'react';
import {useLocation} from 'react-router-dom';

import {envIsPublicCloud} from '../../../config';
import {FORGE_INFERENCE_PATH} from '../../../inference/paths';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {isUsingAccessToken} from '../../../util/projectAccess';
import * as URL from '../../../util/urls';
import {type BreadcrumbPart} from '../../Breadcrumbs';
import {useNavContext} from '../../NavContextProvider';
import {useIsGlobalRegistryEnabled} from '../../Registries/common/hooks';
import {AccountSelectorContext} from './AccountSelectorContextProvider';
import {useNavBreadcrumbsDefaultParts} from './NavBreadcrumbs/NavBreadcrumbsDefault';
import {useNavBreadcrumbsInferenceParts} from './NavBreadcrumbs/NavBreadcrumbsInference';
import {
  useForgeNotebookId,
  useNavBreadcrumbsNotebooksParts,
} from './NavBreadcrumbs/NavBreadcrumbsNotebooks';
import {useNavBreadcrumbsPublishedReportParts} from './NavBreadcrumbs/NavBreadcrumbsPublishedReport';
import {useNavBreadcrumbsRegistriesParts} from './NavBreadcrumbs/NavBreadcrumbsRegistries';
import {
  type NavBreadcrumbsWeaveProps,
  useNavBreadcrumbsWeaveParts,
} from './NavBreadcrumbs/NavBreadcrumbsWeave';
import {useProjObjType, useReportNameAndID} from './NavBreadcrumbs/utils';

/**
 * The breadcrumb trail for the current route, mirroring SearchNav's left-side
 * dispatch. Returns an empty array when no trail should be shown.
 */
export function useNavBreadcrumbParts(): BreadcrumbPart[] {
  const {params, shouldShowBreadcrumbs} = useNavContext();
  const {pathname} = useLocation();
  const {entityName, projectName, reportNameAndID, collectionId, modelId} =
    params;
  const {enableGlobalRegistry} = useIsGlobalRegistryEnabled();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const accountOrgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const projObjType = useProjObjType();
  const {reportName, reportID} = useReportNameAndID({
    reportNameAndID,
    projObjType,
  });
  const isForgeInferencePage =
    pathname === FORGE_INFERENCE_PATH ||
    pathname.startsWith(`${FORGE_INFERENCE_PATH}/`);

  const registriesParts = useNavBreadcrumbsRegistriesParts({
    orgName: accountOrgName,
    registryName: params.registryName,
  });
  const inferenceParts = useNavBreadcrumbsInferenceParts({
    collectionId,
    modelId,
  });
  const forgeNotebookId = useForgeNotebookId();
  const notebookParts = useNavBreadcrumbsNotebooksParts({
    notebookId: forgeNotebookId,
  });
  const publishedReportParts = useNavBreadcrumbsPublishedReportParts({
    entityName: entityName ?? '',
    reportID,
    reportName,
  });
  const weaveParams: NavBreadcrumbsWeaveProps =
    params as NavBreadcrumbsWeaveProps;
  const weaveParts = useNavBreadcrumbsWeaveParts({
    entityName: entityName ?? '',
    projectName: projectName ?? '',
    weaveTab: weaveParams.weaveTab,
    itemName: weaveParams.itemName,
    version: weaveParams.version,
    refExtra: weaveParams.refExtra,
  });
  const defaultParts = useNavBreadcrumbsDefaultParts({
    entityName: entityName ?? '',
    reportName,
    reportID,
    projObjType,
  });

  if (
    entityName == null &&
    URL.isOnRegistriesPage(pathname) &&
    enableGlobalRegistry
  ) {
    return registriesParts;
  }

  if (
    isForgeInferencePage ||
    URL.isOnInferencePage(pathname) ||
    URL.isOnInferenceComparePage(pathname)
  ) {
    return inferenceParts;
  }

  if (forgeNotebookId != null) {
    return notebookParts;
  }

  if (entityName == null || !shouldShowBreadcrumbs) {
    return [];
  }

  if (URL.isOnReportView(pathname) && isUsingAccessToken()) {
    return [];
  }

  if (URL.isOnPublishedReportView(pathname)) {
    return publishedReportParts;
  }

  if (URL.isOnWeavePage(pathname) && projectName != null) {
    return weaveParts;
  }

  return defaultParts;
}
